import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { customerLocationsService } from '../services/customerLocationsService';
import { Customer, CustomerLocation } from '../types';

interface CustomerLocationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer;
}

type LocationFormData = {
  locationName: string;
  address?: string;
};

export const CustomerLocationsModal: React.FC<CustomerLocationsModalProps> = ({
  isOpen,
  onClose,
  customer,
}) => {
  const [locations, setLocations] = useState<CustomerLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<LocationFormData>();

  const loadLocations = async () => {
    try {
      setLoading(true);
      const data = await customerLocationsService.getByCustomerId(customer.id);
      setLocations(data);
    } catch (error) {
      console.error('Failed to load locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && customer?.id) {
      loadLocations();
      reset();
      setEditingId(null);
    }
  }, [isOpen, customer?.id]);

  const onSubmit = async (data: LocationFormData) => {
    try {
      setSubmitting(true);
      if (editingId) {
        await customerLocationsService.update(customer.id, editingId, data);
        toast.success('Location updated');
      } else {
        await customerLocationsService.create(customer.id, data);
        toast.success('Location added');
      }
      reset();
      setEditingId(null);
      loadLocations();
    } catch (error: any) {
      console.error('Failed to save location:', error);
      toast.error(error.response?.data?.message || 'Failed to save location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (location: CustomerLocation) => {
    setEditingId(location.id);
    setValue('locationName', location.locationName);
    setValue('address', location.address || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    reset();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      await customerLocationsService.delete(customer.id, id);
      toast.success('Location deleted');
      loadLocations();
    } catch (error: any) {
      console.error('Failed to delete location:', error);
      toast.error(error.response?.data?.message || 'Failed to delete location');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Locations</h2>
            <p className="text-sm text-gray-500">{customer.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Location List */}
        <div className="mb-6">
          {loading ? (
            <div className="text-center py-4 text-gray-500">Loading...</div>
          ) : locations.length === 0 ? (
            <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
              No locations added yet
            </div>
          ) : (
            <div className="space-y-2">
              {locations.map((location) => (
                <div
                  key={location.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {location.locationName}
                    </div>
                    {location.address && (
                      <div className="text-sm text-gray-500">
                        {location.address}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleEdit(location)}
                      className="p-2 text-blue-600 hover:bg-blue-100 active:bg-blue-200 rounded min-w-[40px] min-h-[40px] flex items-center justify-center"
                      title="Edit"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(location.id)}
                      className="p-2 text-red-600 hover:bg-red-100 active:bg-red-200 rounded min-w-[40px] min-h-[40px] flex items-center justify-center"
                      title="Delete"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add/Edit Form */}
        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {editingId ? 'Edit Location' : 'Add New Location'}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <input
                type="text"
                {...register('locationName', {
                  required: 'Location name is required',
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Location name (e.g.: Kocaeli Aktif Hospital)"
              />
              {errors.locationName && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.locationName.message}
                </p>
              )}
            </div>

            <div>
              <input
                type="text"
                {...register('address')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Address (optional)"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 min-h-[40px] transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 min-h-[40px] transition-colors"
              >
                {submitting
                  ? 'Saving...'
                  : editingId
                  ? 'Update'
                  : 'Add'}
              </button>
            </div>
          </form>
        </div>

        {/* Close Button */}
        <div className="mt-4 sm:mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 font-medium min-h-[44px] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
